import React, { useState } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Github, ExternalLink, ChevronDown, ChevronUp } from 'lucide-react';
import { projects } from '../data/mock';

const ProjectCard = ({ project }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden hover:border-teal-500/50 transition-all duration-300 hover:shadow-xl hover:shadow-teal-500/10">
      <div className="p-8">
        {/* Header */}
        <h3 className="text-2xl font-bold text-white mb-3">{project.title}</h3>
        <p className="text-slate-300 mb-6 leading-relaxed">{project.description}</p>

        {/* Tech Stack */}
        <div className="flex flex-wrap gap-2 mb-6">
          {project.techStack.map((tech, index) => (
            <Badge
              key={index}
              variant="secondary"
              className="bg-teal-500/10 text-teal-400 border border-teal-500/30 hover:bg-teal-500/20"
            >
              {tech}
            </Badge>
          ))}
        </div>

        {/* Expandable Details */}
        {expanded && (
          <div className="space-y-6 mb-6 animate-fade-in">
            <div>
              <h4 className="text-lg font-semibold text-teal-400 mb-3">Architecture & Implementation</h4>
              <ul className="space-y-2">
                {project.architecture.map((item, index) => (
                  <li key={index} className="text-slate-300 flex items-start">
                    <span className="text-teal-400 mr-2">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold text-teal-400 mb-3">Challenges Solved</h4>
              <ul className="space-y-2">
                {project.challenges.map((item, index) => (
                  <li key={index} className="text-slate-300 flex items-start">
                    <span className="text-teal-400 mr-2">✓</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Toggle Button */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-2 text-teal-400 hover:text-teal-300 transition-colors duration-200 mb-6"
        >
          {expanded ? (
            <>
              <span>Show Less</span>
              <ChevronUp className="w-4 h-4" />
            </>
          ) : (
            <>
              <span>Show Architecture & Details</span>
              <ChevronDown className="w-4 h-4" />
            </>
          )}
        </button>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3">
          {project.github && (
            <Button
              asChild
              variant="outline"
              className="border-teal-500 text-teal-400 hover:bg-teal-500/10"
            >
              <a href={project.github} target="_blank" rel="noopener noreferrer">
                <Github className="w-4 h-4 mr-2" />
                View Code
              </a>
            </Button>
          )}
          {project.demo && (
            <Button
              asChild
              className="bg-teal-500 hover:bg-teal-600 text-white"
            >
              <a href={project.demo} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="w-4 h-4 mr-2" />
                Live Demo
              </a>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

const Projects = () => {
  return (
    <section id="projects" className="py-20 bg-slate-900">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Featured Projects
          </h2>
          <p className="text-slate-400 text-lg max-w-3xl mx-auto">
            Real-world applications demonstrating expertise in automation framework development and backend systems
          </p>
        </div>

        <div className="space-y-8">
          {projects.map((project) => (
            <ProjectCard key={project.id} project={project} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;
